import React from 'react';
import { Database, Type, CheckCircle } from 'lucide-react';

interface ColumnInfo {
  name: string;
  type: string;
  examples?: any[];
}

interface ColumnStructureDisplayProps {
  columns: ColumnInfo[];
  showExamples?: boolean;
  compact?: boolean;
  title?: string;
}

const ColumnStructureDisplay: React.FC<ColumnStructureDisplayProps> = ({
  columns,
  showExamples = true,
  compact = false,
  title = "Colonnes de la base de données"
}) => {
  // Séparer les colonnes requises (ref, nom) des colonnes d'attributs
  const requiredColumns = columns.filter(
    col => col.name === 'ref' || col.name === 'nom'
  );
  const attributeColumns = columns.filter(
    col => col.name !== 'ref' && col.name !== 'nom'
  );

  const getTypeColor = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'TEXT':
        return 'bg-blue-100 text-blue-800';
      case 'NUMERIC':
      case 'INTEGER':
        return 'bg-green-100 text-green-800';
      case 'BOOLEAN':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Database size={14} />
          <span>{title}</span>
        </div>
        <div className="space-y-2">
          {/* Colonnes requises */}
          {requiredColumns.length > 0 && (
            <div>
              <div className="text-xs text-gray-600 mb-1 font-medium">Colonnes requises :</div>
              <div className="flex flex-wrap gap-2">
                {requiredColumns.map((col) => (
                  <div
                    key={col.name}
                    className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs"
                  >
                    <CheckCircle size={12} className="text-green-600" />
                    <span className="font-mono font-medium">{col.name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${getTypeColor(col.type)}`}>
                      {col.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Colonnes d'attributs */}
          {attributeColumns.length > 0 && (
            <div>
              <div className="text-xs text-gray-600 mb-1 font-medium">Colonnes d'attributs :</div>
              <div className="flex flex-wrap gap-2">
                {attributeColumns.map((col) => (
                  <div
                    key={col.name}
                    className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs"
                  >
                    <span className="font-mono font-medium">{col.name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${getTypeColor(col.type)}`}>
                      {col.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {columns.length === 0 && (
            <p className="text-xs text-gray-500">Aucune colonne définie</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Database className="text-blue-600" size={18} />
        <h4 className="font-medium text-gray-900">{title}</h4>
      </div>
      
      {/* Colonnes requises */}
      {requiredColumns.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-600" size={16} />
            <h5 className="text-sm font-semibold text-gray-900">Colonnes requises</h5>
          </div>
          {requiredColumns.map((col) => (
            <div
              key={col.name}
              className="p-3 bg-green-50 border border-green-200 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-green-600" size={14} />
                  <code className="text-sm font-mono font-semibold text-gray-900">
                    {col.name}
                  </code>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(col.type)}`}>
                    {col.type}
                  </span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                    Requise
                  </span>
                </div>
              </div>
              
              {showExamples && col.examples && col.examples.length > 0 && (
                <div className="mt-2 pt-2 border-t border-green-300">
                  <div className="text-xs text-gray-600 mb-1">Exemples de valeurs :</div>
                  <div className="flex flex-wrap gap-1">
                    {col.examples.map((example, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-white border border-green-300 rounded text-xs font-mono text-gray-700"
                      >
                        {String(example)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Colonnes d'attributs */}
      {attributeColumns.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Type className="text-blue-600" size={16} />
            <h5 className="text-sm font-semibold text-gray-900">Colonnes d'attributs</h5>
          </div>
          {attributeColumns.map((col) => (
            <div
              key={col.name}
              className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono font-semibold text-gray-900">
                    {col.name}
                  </code>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(col.type)}`}>
                    {col.type}
                  </span>
                </div>
              </div>
              
              {showExamples && col.examples && col.examples.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="text-xs text-gray-600 mb-1">Exemples de valeurs :</div>
                  <div className="flex flex-wrap gap-1">
                    {col.examples.map((example, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono text-gray-700"
                      >
                        {String(example)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {columns.length === 0 && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">
            Aucune colonne définie dans cette base de données.
          </p>
        </div>
      )}
      
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Important :</strong> Les fichiers d'import doivent contenir les colonnes requises ({requiredColumns.map(c => c.name).join(', ')}) 
          et peuvent contenir les colonnes d'attributs listées ci-dessus. 
          Les colonnes supplémentaires non présentes dans la base seront rejetées.
        </p>
      </div>
    </div>
  );
};

export default ColumnStructureDisplay;
